import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CommonDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = "sm",
  fullWidth = true,
  variant = "standard",
  icon,
  iconBgColor = "#FDF2F2",
}) => {
  const isAlert = variant === "alert";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isAlert ? false : maxWidth}
      fullWidth={isAlert ? false : fullWidth}
      slotProps={{
        paper: {
          sx: isAlert
            ? {
                borderRadius: "16px",
                width: "100%",
                maxWidth: "440px",
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.08)",
                backgroundColor: "#FFFFFF",
              }
            : {
                borderRadius: "16px",
                backgroundColor: "#F7F8FB",
                maxHeight: "90vh",
                m: 1,
                p: 2,
              },
        },
      }}
    >
      {isAlert ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          {icon && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: iconBgColor,
                borderRadius: "50%",
                width: "56px",
                height: "56px",
                mb: 2.5,
              }}
            >
              {icon}
            </Box>
          )}

          {title && (
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: "#10100F",
                mb: 1.5,
              }}
            >
              {title}
            </Typography>
          )}

          {children && (
            <Typography
              component="div"
              sx={{
                fontSize: "14px",
                color: "#403C6F",
                fontWeight: 500,
                lineHeight: "1.5",
                mb: 3,
                px: 1,
              }}
            >
              {children}
            </Typography>
          )}

          {actions && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "90vh",
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              m: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "#10100F",
              }}
            >
              {title}
            </Typography>
            <IconButton
              onClick={onClose}
              aria-label="close"
              sx={{
                color: "#403C6F",
                p: 0.5,
                "&:hover": {
                  backgroundColor: "rgba(64, 60, 111, 0.04)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, overflowY: "auto", mb: actions ? 2 : 0 }}>
            {children}
          </DialogContent>

          {actions && (
            <DialogActions
              sx={{ p: 0, mt: "auto", justifyContent: "flex-end" }}
            >
              {actions}
            </DialogActions>
          )}
        </Box>
      )}
    </Dialog>
  );
};

CommonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  fullWidth: PropTypes.bool,
  variant: PropTypes.oneOf(["standard", "alert"]),
  icon: PropTypes.node,
  iconBgColor: PropTypes.string,
};

export default CommonDialog;
